"use client";
import { ChevronDown } from "lucide-react";

import { DATA_COVERAGE_LABELS, coverageDates, coverageFloor, type DataCoverage, type DataCoverageSource } from "@/lib/data-coverage";
import { useNowMs } from "./use-now";

/**
 * Строка над заголовком: «Изменено 5 минут назад · данные до 22 июля».
 *
 * «Данные до» — по какое число реально есть цифры. Само по себе время
 * обновления Ozon этого не говорит: обновление минуту назад спокойно привозит
 * цифры позавчерашним числом, потому что площадка отдаёт их с задержкой, —
 * человек видит свежее время и считает последнюю неделю полной. Само время
 * обновления живёт в шапке справа, здесь его нет: два одинаковых «обновлено»
 * на одном экране только путают.
 *
 * «Изменено» — про САМУ страницу: текст, блоки, что угодно. Это то, что нужно
 * знать про документ, и это не то же самое, что свежесть цифр Ozon.
 *
 * Показываем САМУЮ РАННЮЮ дату из источников: «до 22 июля» честно значит «до
 * этого числа полно всё». Разбивка по источникам — в подсказке при наведении,
 * чтобы строка оставалась одной строкой.
 *
 * Строка нарочно тихая и всегда одного цвета. Отставание данных подсвечивать
 * цветом пробовали — получалась тревога там, где её нет: сутки задержки у Ozon
 * это норма, а цветная строка выглядит как поломка. Про заметное отставание
 * сказано словами в подсказке, а кричать об этом должно обновление в шапке.
 */

/** Тихий серый: строку находят, когда она понадобилась, а не читают каждый раз. */
const MUTED = "#a3a19b";

/** Насколько дней данные могут отставать, прежде чем это перестанет быть нормой. */
const STALE_DAYS = 2;

const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];

/** «2026-07-22» → «22 июля» (и с годом, если год не текущий). */
function humanDate(iso: string, now: Date): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  const base = `${d} ${MONTHS[m - 1] ?? ""}`.trim();
  return y === now.getFullYear() ? base : `${base} ${y}`;
}

/** Разница в целых днях между датой-строкой и сегодня (по календарю, не по часам). */
function daysBehind(iso: string, now: Date): number {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return 0;
  const then = Date.UTC(y, m - 1, d);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((today - then) / 86_400_000);
}

function agoLabel(ts: number, now: number): string {
  const mins = Math.max(0, Math.round((now - ts) / 60_000));
  if (mins < 1) return "только что";  // «0 мин назад» звучит как поломка
  if (mins < 60) return `${mins} мин назад`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)} ч назад`;
  return `${Math.round(mins / 1440)} дн назад`;
}

export type PageStoreChip = {
  name: string; pinned?: boolean;
  onOpen?: (anchor?: HTMLElement) => void;
};

export default function DataFreshness({ coverage, changedAt, className = "", store }: {
  coverage?: DataCoverage | null;
  /** Когда последний раз правили саму страницу (миллисекунды). */
  changedAt?: number | null;
  className?: string;
  /** Магазин страницы — рядом с «Изменено…», среди свойств самой страницы.
   *  В шапке сервиса он читался как рубильник на весь сервис. */
  store?: PageStoreChip | null;
}) {
  const nowMs = useNowMs();
  if (!nowMs) return null;
  const now = new Date(nowMs);

  const floor = coverage ? coverageFloor(coverage) : null;
  const parts: string[] = [];
  if (changedAt) parts.push(`Изменено ${agoLabel(changedAt, now.getTime())}`);
  if (floor) parts.push(`данные до ${humanDate(floor, now)}`);
  if (parts.length === 0 && !store) return null;

  // Отставание влияет только на текст подсказки — цвет строки всегда один.
  const newest = coverage ? coverageDates(coverage).reduce((max, d) => (d > max ? d : max), "") : "";
  const stale = !!newest && daysBehind(newest, now) > STALE_DAYS;

  const tip = [
    ...(coverage
      ? (Object.keys(DATA_COVERAGE_LABELS) as DataCoverageSource[])
          .filter(key => coverage[key])
          .map(key => `${DATA_COVERAGE_LABELS[key]}: до ${humanDate(coverage[key]!, now)}`)
      : []),
    ...(coverage?.notes ?? []),
    ...(changedAt ? [`Страница изменена: ${new Date(changedAt).toLocaleString("ru-RU")}`] : []),
    ...(stale ? ["Данные отстают больше обычного — запустите обновление."] : []),
  ].join("\n");

  return (
    <div className={`flex flex-wrap items-center gap-2 text-[11.5px] ${className}`} style={{ color: MUTED }}>
      {store && (
        <button data-page-store-chip type="button" onClick={event => store.onOpen?.(event.currentTarget)} disabled={!store.onOpen}
          aria-haspopup={store.onOpen ? "dialog" : undefined}
          title={store.onOpen ? "Магазин этой страницы — нажмите, чтобы сменить" : "Магазин этой страницы"}
          className="flex items-center gap-1.5 rounded-md px-1.5 py-[3px] transition-colors enabled:hover:bg-[#EFEDE6] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#8D897F]"
          style={{ color: "#6b6760", boxShadow: `inset 0 0 0 1px ${store.pinned ? "#E5E1D6" : "transparent"}` }}>
          <span className="flex h-[15px] min-w-[15px] items-center justify-center rounded px-[3px] text-[9.5px] font-bold"
            style={{ background: "#EFEDE6", color: "#7d786e" }}>
            {(store.name[0] || "S").toUpperCase()}
          </span>
          {store.name}
          {store.onOpen && <ChevronDown size={11} strokeWidth={1.7} className="opacity-60" />}
        </button>
      )}
      {parts.length > 0 && <span title={tip || undefined}>{parts.join(" · ")}</span>}
    </div>
  );
}
